import { Alert, Button, Card, CardBody, CardTitle, Dropdown, DropdownItem, DropdownToggle, ExpandableSection, Modal } from "@patternfly/react-core";
import React, { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { supportedLanguages } from "../../i18n";
import { requestLogOut, resetModelCustomization, StoreState } from "../../store/reducers";
import { backendTierAppAuths } from "../../web-apis/cloud-backend-auth";

import './UserSettings.css';
import { getUserName } from "../../store/mvision-client-list";

const UserSettings = () => {

    const { t, i18n } = useTranslation();

    const dispatch = useDispatch();
    const onLogOutClick = useCallback(() => {
        dispatch(requestLogOut());
    }, [dispatch]);

    const appClient = useSelector((state: StoreState) => state.currentAppClient);

    const [isLangDropdownOpen, setIsLangDropdownOpen] = React.useState(false);
    const [isResetBoxExpanded, setIsResetBoxExpanded] = React.useState(false);
    const [isResetConfirmationModalOpen, setIsResetConfirmationModalOpen] = React.useState(false);
    const [isResetCompletedTextVisible, setIsResetCompletedTextVisible] = React.useState(false);
    const [isResetting, setIsResetting] = React.useState(false);

    const deploymentInfo = useSelector((state: StoreState) => state.appDeploymentInfo);
    const isResetInProgress = useSelector((state: StoreState) => state.isModelCustomizationDeleteInProgress);

    const handleLangDropdownToggle = useCallback((isOpen: boolean) => {
        setIsLangDropdownOpen(isOpen);
    }, []);

    const onLangSelect = useCallback((nextLang: string) => {
        const langKey = supportedLanguages.find(l => l.label === nextLang)?.langKey;
        i18n.changeLanguage(langKey);
    }, [i18n]);

    const handleLangSelect = useCallback((value: any) => {
        setIsLangDropdownOpen(false);
        onLangSelect(value.target.innerText)
    }, [onLangSelect]);

    const handleExpandResetBoxClicked = useCallback((isExpanded: boolean) => {
        setIsResetBoxExpanded(isExpanded);
    }, []);

    const handleResetButtonClicked = useCallback(() => {
        setIsResetConfirmationModalOpen(true);
    }, []);

    const handleCloseResetModalClicked = useCallback(() => {
        setIsResetConfirmationModalOpen(false);
    }, []);

    const handleConfirmResetClicked = useCallback(() => {
        if (!isResetInProgress) {
            dispatch(resetModelCustomization(appClient));
        }
        setIsResetConfirmationModalOpen(false);
        setIsResetting(true);
        setIsResetCompletedTextVisible(false);
    }, [appClient, dispatch, isResetInProgress]);

    useEffect(() => {
        if (isResetting && !isResetInProgress) {
            // show reset completed text once it's done
            setIsResetCompletedTextVisible(true);
        }
    }, [isResetInProgress, isResetting]);

    const loggedInUsernames = Object.values(backendTierAppAuths)
        .filter(appAuth => appAuth.isLoggedIn)
        .map(appAuth => ({ appName: appAuth.appName, username: appAuth.loggedInUsername }));

    // show supported languages, but filter out languages set as omitted from UI in config.json
    const langItems = supportedLanguages.filter(l => !deploymentInfo || !deploymentInfo.omitLocalizationsFromUi || !deploymentInfo.omitLocalizationsFromUi.includes(l.langKey))
        .map(l => (<DropdownItem key={l.langKey} value={l.langKey}>{l.label}</DropdownItem>))
    const currentLanguage = supportedLanguages.find(l => l.langKey === i18n.resolvedLanguage)?.label;

    return (
        <div className="user-settings">
            {loggedInUsernames.length > 0 &&
                (
                    <div className="setting-item" data-test='settings' data-testid='logged-in-as'>
                        <span className="label">{t('settings.loggedInAs')}</span>
                        <span className="value">
                            {loggedInUsernames.length > 1 ?
                                loggedInUsernames.map(appNameAndUser => (<div key={appNameAndUser.appName}>{appNameAndUser.username} ({appNameAndUser.appName})</div>))
                                : loggedInUsernames[0].username}
                        </span>
                        <span className="log-out-button"><Button variant="danger" onClick={onLogOutClick}>{t('common.logOut')}</Button></span>
                    </div>
                )
            }

            <div className="setting-item" data-test='settings' data-testid='language'>
                <span className="label">{t('settings.language')}</span>
                <span className="value">
                    <Dropdown
                        dropdownItems={langItems}
                        toggle={(
                            <DropdownToggle onToggle={handleLangDropdownToggle}>
                                {currentLanguage}
                            </DropdownToggle>
                        )}
                        isOpen={isLangDropdownOpen}
                        onSelect={handleLangSelect}
                    />
                </span>
            </div>

            {appClient ? (
                <Card isFlat className="reset-to-factory-settings-box" data-test='settings' data-testid='reset-settings'>
                    <CardTitle>Settings for Configuration Target: <span className="configuration-target-box-name">{getUserName(appClient)}</span></CardTitle>
                    <CardBody>
                        <ExpandableSection toggleContent={(<b>Reset configuration</b>)} onToggle={handleExpandResetBoxClicked} isExpanded={isResetBoxExpanded} displaySize="large" isWidthLimited>
                            <div className="setting-item">
                                <span className="label">Reset all model configuration to factory settings</span>
                                <span className="value">
                                    <Button variant="danger" onClick={handleResetButtonClicked} isDisabled={isResetInProgress} isLoading={isResetInProgress}>Reset configuration</Button>
                                </span>
                            </div>

                            {isResetCompletedTextVisible && (
                                <div className="configuration-reset-completed-box">
                                    <Alert variant="info" isInline title="Configuration reset was completed." />
                                </div>
                            )}

                            <div>Resetting model configuration to factory settings will remove all current model customizations and model selections, including any unsaved changes, and return them back to default values. This operation <b>cannot be reverted.</b></div>
                        </ExpandableSection>
                    </CardBody>
                </Card>
            ) : (
                <Card isFlat className="reset-to-factory-settings-box" data-test='settings' data-testid='reset-settings'>
                    <CardTitle>Settings for Configuration Target</CardTitle>
                    <CardBody>
                        <div>Select a Configuration Target to view settings specific to it in here.</div>
                    </CardBody>
                </Card>
            )}

            <Modal
                isOpen={isResetConfirmationModalOpen}
                onClose={handleCloseResetModalClicked}
                variant={"medium"}
                title='Confirm reset to default factory configuration'
                actions={[
                    <Button data-test='settings-modal' data-testid='reset' key="confirm-reset" onClick={handleConfirmResetClicked} variant="danger">Reset all configuration</Button>,
                    <Button data-test='settings-modal' data-testid='cancel' key="cancel-reset" onClick={handleCloseResetModalClicked}>Cancel</Button>
                ]}
                className="reset-confirmation-modal">
                <div>You are about to reset configuration settings for configuration target <span className="reset-emphasis configuration-target-name">{appClient ? getUserName(appClient) : ''}</span>.</div>
                <div>Resetting model configuration to factory settings will remove all current model customizations and model selections, including any unsaved changes, and return them back to default values. This operation <span className="reset-emphasis">cannot be reverted.</span></div>
            </Modal>

        </div>
    );
}

export default UserSettings;
